import { useQuery } from "@apollo/client/react";
import {
  ActionIcon,
  Badge,
  Card,
  Collapse,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { type FragmentOf, readFragment } from "gql.tada";
import { useState } from "react";

import {
  JOURNAL_ENTRY_PAYLOAD_QUERY,
  JournalEntryPayloadFragment,
  JournalEntrySummaryFragment,
} from "./JournalFeed.graphql";
import { JournalActorContextRenderer } from "./JournalActorContextRenderer";
import { JournalPayloadRenderer } from "./JournalPayloadRenderer";

export type JournalEntryCardEntry = FragmentOf<
  typeof JournalEntrySummaryFragment
>;

type UnmaskedEntry = ReturnType<
  typeof readFragment<typeof JournalEntrySummaryFragment, JournalEntryCardEntry>
>;
type Severity = UnmaskedEntry["severity"];

const SEVERITY_COLORS: Record<Severity, string> = {
  DEBUG: "gray",
  INFO: "blue",
  WARNING: "yellow",
  ERROR: "red",
};

// gql.tada maps DateTime to `unknown`; narrow once at the render boundary
// instead of asserting an unsafe cast across the component tree.
function formatJournalDate(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleString();
  }
  return "";
}

interface Props {
  entry: JournalEntryCardEntry;
  children?: ReadonlyArray<JournalEntryCardEntry>;
}

export function JournalEntryCard({ entry, children }: Props) {
  const [open, setOpen] = useState(false);
  const unmasked = readFragment(JournalEntrySummaryFragment, entry);

  const { data: payloadData, loading: payloadLoading } = useQuery(
    JOURNAL_ENTRY_PAYLOAD_QUERY,
    {
      variables: { id: unmasked.id },
      skip: !open,
      fetchPolicy: "cache-first",
    },
  );

  const payload = payloadData?.journalEntry
    ? readFragment(JournalEntryPayloadFragment, payloadData.journalEntry)
        .payload
    : null;

  const createdAtLabel = formatJournalDate(unmasked.createdAt);

  return (
    <Card withBorder p="xs" radius="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
          <Group gap="xs" wrap="nowrap">
            <Badge color={SEVERITY_COLORS[unmasked.severity]} size="xs">
              {unmasked.severity}
            </Badge>
            <Text size="xs" c="dimmed">
              {createdAtLabel}
            </Text>
            <Text size="sm" fw={500} truncate>
              {unmasked.type}
            </Text>
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              Actor: {unmasked.actor.type}
            </Text>
            <JournalActorContextRenderer context={unmasked.actor.context} />
          </Group>
        </Stack>
        <ActionIcon
          variant="subtle"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Collapse entry" : "Expand entry"}
        >
          {open ? <IconChevronUp /> : <IconChevronDown />}
        </ActionIcon>
      </Group>
      <Collapse in={open}>
        <Stack gap="xs" mt="sm">
          {payloadLoading && !payload && <Loader size="xs" />}
          {payload && <JournalPayloadRenderer payload={payload} />}
          {children && children.length > 0 && (
            <Stack
              gap={4}
              pl="md"
              style={{ borderLeft: "2px solid var(--mantine-color-gray-3)" }}
            >
              <Text size="xs" fw={500} c="dimmed">
                Effects ({children.length}):
              </Text>
              {children.map((child) => {
                const childUnmasked = readFragment(
                  JournalEntrySummaryFragment,
                  child,
                );
                return <JournalEntryCard key={childUnmasked.id} entry={child} />;
              })}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Card>
  );
}
